// Talent test platform API client — thin fetch wrappers over /api/seller/*.
//
// Mirrors admin-internal's local `apiFetch` (the shared `createApiClient` only exposes
// the client turn calls). Stamps the stub `x-seller-id` identity header for the
// currently-simulated talent and hits the absolute backend origin. All response
// shapes are the GENERATED wire types from @recruiter/shared (backend
// `web/routes/seller.py` is the source of truth), never hand-written.

import type {
  CreateDebugNoteResponse,
  DeleteAllResponse,
  PostSellerMessageResponse,
  SellerExportResponse,
  SellerFileUploadResponse,
  SellerProjectList,
  SellerThreadDetail,
  SellerThreadList,
} from "@recruiter/shared";

import { SELLER_API_BASE, sellerAuthHeaders } from "../config";
import { talentDebugBuyerId } from "./debug-note";

/** Absolute URL for a talent document link (the backend returns a relative
 * path with the `seller_id` query param already baked in, so a plain new-tab
 * navigation authenticates). Prepends the backend origin for cloud/local. */
export function sellerDocUrl(relativePath: string): string {
  return `${SELLER_API_BASE}${relativePath}`;
}

async function apiFetch(path: string, sellerId: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  for (const [k, v] of Object.entries(sellerAuthHeaders(sellerId))) {
    headers.set(k, v);
  }
  return fetch(`${SELLER_API_BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
}

export type SellerFetchOutcome<T> =
  | { ok: true; data: T }
  | { ok: false; status: 401 | 403 | 404 | "error"; message: string };

async function readJson<T>(r: Response): Promise<SellerFetchOutcome<T>> {
  if (r.status === 401) {
    return { ok: false, status: 401, message: "Pick a talent identity first." };
  }
  if (r.status === 403) {
    return { ok: false, status: 403, message: "Not this talent's thread." };
  }
  if (r.status === 404) {
    return { ok: false, status: 404, message: "Thread not found." };
  }
  if (!r.ok) {
    return { ok: false, status: "error", message: `Request failed (${r.status})` };
  }
  return { ok: true, data: (await r.json()) as T };
}

/** The project-first picker: every concierge project (run), live ones first,
 * then closed ones, each newest-active first, with its talents embedded. No
 * identity needed (this is how you choose one). */
export async function fetchSellerProjects(): Promise<SellerFetchOutcome<SellerProjectList>> {
  try {
    return await readJson<SellerProjectList>(await apiFetch("/api/seller/projects", ""));
  } catch (e) {
    return { ok: false, status: "error", message: (e as Error).message };
  }
}

/** Wipe ALL seller/concierge test data (every run + its threads/messages/
 * offers). Destructive, testing-only — mirrors the no-identity directory. */
export async function deleteAllSellers(): Promise<SellerFetchOutcome<DeleteAllResponse>> {
  try {
    return await readJson<DeleteAllResponse>(
      await apiFetch("/api/seller/sellers", "", { method: "DELETE" }),
    );
  } catch (e) {
    return { ok: false, status: "error", message: (e as Error).message };
  }
}

export async function fetchSellerThreads(
  sellerId: string,
): Promise<SellerFetchOutcome<SellerThreadList>> {
  try {
    return await readJson<SellerThreadList>(await apiFetch("/api/seller/threads", sellerId));
  } catch (e) {
    return { ok: false, status: "error", message: (e as Error).message };
  }
}

export async function fetchSellerThread(
  threadId: string,
  sellerId: string,
): Promise<SellerFetchOutcome<SellerThreadDetail>> {
  try {
    return await readJson<SellerThreadDetail>(
      await apiFetch(`/api/seller/threads/${encodeURIComponent(threadId)}`, sellerId),
    );
  } catch (e) {
    return { ok: false, status: "error", message: (e as Error).message };
  }
}

export async function postSellerMessage(
  threadId: string,
  body: string,
  sellerId: string,
  attachmentFileIds: string[] = [],
): Promise<SellerFetchOutcome<PostSellerMessageResponse>> {
  try {
    const r = await apiFetch(
      `/api/seller/threads/${encodeURIComponent(threadId)}/messages`,
      sellerId,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ body, attachment_file_ids: attachmentFileIds }),
      },
    );
    return await readJson<PostSellerMessageResponse>(r);
  } catch (e) {
    return { ok: false, status: "error", message: (e as Error).message };
  }
}

/** Upload one or more files the talent is attaching to a thread. Multipart (the
 * browser sets the boundary — do NOT set content-type). Returns the stored file
 * ids (to attach to the next message) + any per-file rejections. */
export async function uploadSellerFile(
  threadId: string,
  files: File[],
  sellerId: string,
): Promise<SellerFetchOutcome<SellerFileUploadResponse>> {
  try {
    const form = new FormData();
    for (const f of files) form.append("files", f);
    const r = await apiFetch(
      `/api/seller/threads/${encodeURIComponent(threadId)}/files`,
      sellerId,
      { method: "POST", body: form },
    );
    return await readJson<SellerFileUploadResponse>(r);
  } catch (e) {
    return { ok: false, status: "error", message: (e as Error).message };
  }
}

/** Ask the backend to render one of the talent's thread documents as a PDF.
 *
 * `exportPath` is the `export_url` the message document carries — a POST, not a
 * download. It returns 202 the moment the job is on the queue; the bytes arrive
 * later as a `seller.export.ready` Pusher event carrying the download URL, which
 * the store folds into the button's state. */
export async function requestSellerExport(
  exportPath: string,
  sellerId: string,
): Promise<SellerFetchOutcome<SellerExportResponse>> {
  try {
    return await readJson<SellerExportResponse>(
      await apiFetch(exportPath, sellerId, { method: "POST" }),
    );
  } catch (e) {
    return { ok: false, status: "error", message: (e as Error).message };
  }
}

/** Tell the backend the talent is composing a reply — stamps `typing_at` and
 * nudges the client dashboard's "Typing now" KPI over Pusher.
 *
 * Fire-and-forget by design: the live count is delivered to the CLIENT (not the
 * talent), so there's nothing to read here, and a dropped ping self-heals
 * server-side (the count is recomputed on the next snapshot). We deliberately
 * don't surface errors for an ephemeral typing signal. */
export async function postSellerTyping(threadId: string, sellerId: string): Promise<void> {
  try {
    await apiFetch(`/api/seller/threads/${encodeURIComponent(threadId)}/typing`, sellerId, {
      method: "POST",
    });
  } catch {
    // Ephemeral telemetry — never block or alarm the tester over a typing ping.
  }
}

/** Log a tester's debug note (red "Debug" toggle ON) to the admin "Debug Notes"
 * tab — the talent-side twin of the buyer app's `createDebugNote`. Multipart so
 * it can carry the auto-captured screenshot (the browser sets the boundary — do
 * NOT set content-type). Unlike the other calls here it stamps `X-Buyer-Id`
 * (a self-describing `guest_talent_*` id) rather than `X-Seller-Id`, because the
 * `/api/debug-notes` route is buyer-scoped (`require_buyer`); see
 * `talentDebugBuyerId`. `stage` is a free-text descriptor of the current view. */
export async function postDebugNote(input: {
  note: string;
  sellerId: string;
  stage: string;
  screenshot: Blob | null;
}): Promise<SellerFetchOutcome<CreateDebugNoteResponse>> {
  try {
    const form = new FormData();
    form.set("note", input.note);
    form.set("stage", input.stage);
    if (input.screenshot) form.set("screenshot", input.screenshot, "screenshot.jpg");
    const r = await fetch(`${SELLER_API_BASE}/api/debug-notes`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "x-buyer-id": talentDebugBuyerId(input.sellerId),
      },
      body: form,
      credentials: "include",
    });
    return await readJson<CreateDebugNoteResponse>(r);
  } catch (e) {
    return { ok: false, status: "error", message: (e as Error).message };
  }
}
